import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PortalV2Layout from './PortalV2Layout';
import { StatusPill } from './AdminV2Dashboard';
import { useAuth } from '../contexts/AuthContext';
import { Search, Briefcase, Mail, Eye, RefreshCw, Zap } from 'lucide-react';

const PAGE_SIZE = 50;

const generateConfirmationEmailUrl = (job) => {
  const jobNumber = job.servemanager_job_number || '';
  const recipientName = job.recipient_name || 'Recipient';
  const clientEmail = job.client_email || '';
  const caseNumber = job.case_number || 'N/A';
  const caseTitle = job.case_title || 'N/A';
  const subject = `Job ${jobNumber} - ${recipientName}`;
  const body = `This email confirms that we have received your service request. All future correspondence regarding this job will be sent through this email thread.

JOB DETAILS:
Case Number: ${caseNumber}
Case Title: ${caseTitle}

You can also track the status of this job in real-time through our Client Portal at https://westexlegal.com/portal

If you have any questions, please reply to this email or contact us directly.`;
  return `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(clientEmail)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const AdminV2Jobs = () => {
  const { authFetch } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [archivedFilter, setArchivedFilter] = useState('active');
  const [page, setPage] = useState(1);
  const [refreshTick, setRefreshTick] = useState(0);
  const [rushOnly, setRushOnly] = useState(false);

  useEffect(() => {
    const handler = () => setRefreshTick(t => t + 1);
    window.addEventListener('pv2-jobs-refresh', handler);
    return () => window.removeEventListener('pv2-jobs-refresh', handler);
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ page, limit: PAGE_SIZE });
        if (statusFilter !== 'all') params.set('status', statusFilter);
        if (archivedFilter !== 'all') params.set('archived', archivedFilter);
        if (search) params.set('search', search);
        if (rushOnly) params.set('rush', 'true');
        const res = await authFetch(`/api/admin/jobs?${params}`);
        if (!active) return;
        if (res.ok) {
          const d = await res.json();
          setJobs(d.jobs || []);
          setTotal(d.total || (d.jobs || []).length);
        }
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [authFetch, page, statusFilter, archivedFilter, search, refreshTick, rushOnly]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <PortalV2Layout
      eyebrow="Operations"
      title="All Jobs"
      subtitle={`${total.toLocaleString()} total jobs across all clients`}
      crumbs={<><span style={{ opacity: 0.5 }}>Admin /</span> <strong style={{ marginLeft: 6 }}>Jobs</strong></>}
      action={
        <button
          onClick={() => { setPage(p => p); setSearch(s => s); /* trigger fetch */ window.dispatchEvent(new Event('pv2-jobs-refresh')); }}
          className="pv2-btn pv2-btn-ghost"
          data-testid="pv2-admin-jobs-refresh"
        >
          <RefreshCw size={14} strokeWidth={1.5} className={loading ? 'pv2-skeleton' : ''} /> Refresh
        </button>
      }
    >
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={{ position: 'relative', flex: '1 1 280px' }}>
          <Search size={14} strokeWidth={1.5} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--pv2-text-subtle)' }} />
          <input className="pv2-input" placeholder="Search by job # or recipient…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ paddingLeft: 36 }} data-testid="pv2-admin-jobs-search" />
        </div>
        <select className="pv2-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} style={{ flex: '0 0 200px' }}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="served">Served</option>
          <option value="bad_address">Bad Address</option>
          <option value="canceled">Canceled</option>
          <option value="closed">Closed</option>
        </select>
        <select className="pv2-select" value={archivedFilter} onChange={(e) => { setArchivedFilter(e.target.value); setPage(1); }} style={{ flex: '0 0 160px' }}>
          <option value="active">Only Active</option>
          <option value="archived">Only Archived</option>
          <option value="all">All Jobs</option>
        </select>
        <button
          onClick={() => { setRushOnly(v => !v); setPage(1); }}
          data-testid="pv2-admin-jobs-rush-filter"
          className="pv2-btn"
          style={{
            flex: '0 0 auto',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            border: '1px solid ' + (rushOnly ? 'var(--pv2-red-bright)' : 'var(--pv2-ink-line)'),
            background: rushOnly ? 'var(--pv2-red-bright)' : 'transparent',
            color: rushOnly ? '#fff' : 'var(--pv2-text-muted)',
          }}
        >
          <Zap size={13} strokeWidth={2} /> Rush only
        </button>
      </div>

      <Pagination page={page} pageCount={pageCount} setPage={setPage} total={total} pageSize={PAGE_SIZE} testIdPrefix="pv2-pag-top" />

      <div className="pv2-table-wrap">
        <table className="pv2-table" data-testid="pv2-admin-jobs-table">
          <thead>
            <tr>
              <th>Job #</th>
              <th>Recipient</th>
              <th>Client</th>
              <th>Case #</th>
              <th>Status</th>
              <th>Created</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="pv2-table-empty">Loading…</td></tr>}
            {!loading && jobs.length === 0 && (
              <tr><td colSpan={7} className="pv2-table-empty">
                <Briefcase size={24} style={{ opacity: 0.3, marginBottom: 8 }} />
                No jobs match these filters.
              </td></tr>
            )}
            {!loading && jobs.map(j => {
              const addr = j.service_address || j.address || {};
              const cityState = [addr.city, addr.state].filter(Boolean).join(', ');
              return (
                <tr key={j.id || j.servemanager_job_id}
                  data-testid={j.is_rush ? `pv2-rush-row-${j.id || j.servemanager_job_id}` : undefined}
                  style={j.is_rush ? { background: 'rgba(248, 113, 113, 0.07)', boxShadow: 'inset 3px 0 0 0 var(--pv2-red-bright)' } : undefined}>
                  <td>
                    <Link to={`/portal/jobs/${j.id || j.servemanager_job_id}`} className="pv2-table-job-num">{j.servemanager_job_number || '—'}</Link>
                  </td>
                  <td style={{ maxWidth: 320 }}>
                    <div style={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.recipient_name || ''}>{j.recipient_name || '—'}</div>
                    {(cityState || j.assigned_server) && (
                      <div style={{ display: 'flex', alignItems: 'baseline', fontSize: 11, marginTop: 2 }}>
                        <span style={{ flex: '0 0 130px', width: 130, color: 'var(--pv2-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cityState}>{cityState}</span>
                        {j.assigned_server && (
                          <Link
                            to={`/portal/admin/server/${encodeURIComponent(j.assigned_server)}`}
                            style={{ color: 'var(--pv2-red-bright)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none' }}
                            title={`View all jobs for ${j.assigned_server}`}
                            data-testid="pv2-job-assigned-server"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {j.assigned_server}
                          </Link>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{ maxWidth: 240 }}>
                    <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.client_company_name || j.client_company || ''}>{j.client_company_name || j.client_company || '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--pv2-text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.client_email || ''}>{j.client_email || ''}</div>
                  </td>
                  <td className="pv2-mono" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12, color: 'var(--pv2-text-muted)' }}>
                    {j.case_number || '—'}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}><StatusPill status={j.service_status || j.job_status} /></td>
                  <td className="pv2-mono" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 11, whiteSpace: 'nowrap' }}>
                    {fmtDate(j.created_at || j.received_at)}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <a
                      href={generateConfirmationEmailUrl(j)}
                      target="_blank" rel="noopener noreferrer"
                      title="Send confirmation email"
                      data-testid={`pv2-email-${j.id || j.servemanager_job_id}`}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, color: 'var(--pv2-text-muted)' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Mail size={14} strokeWidth={1.5} />
                    </a>
                    <Link
                      to={`/portal/jobs/${j.id || j.servemanager_job_id}`}
                      title="View details"
                      data-testid={`pv2-view-${j.id || j.servemanager_job_id}`}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, color: 'var(--pv2-red-bright)', marginLeft: 4 }}
                    >
                      <Eye size={14} strokeWidth={1.5} />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div style={{ marginTop: 20 }}>
          <Pagination page={page} pageCount={pageCount} setPage={setPage} total={total} pageSize={PAGE_SIZE} testIdPrefix="pv2-pag-bot" />
        </div>
      )}
    </PortalV2Layout>
  );
};

const Pagination = ({ page, pageCount, setPage, total, pageSize, testIdPrefix }) => {
  if (pageCount <= 1) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', border: '1px solid var(--pv2-ink-line)', background: 'var(--pv2-ink)', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
      <span style={{ fontSize: 12, color: 'var(--pv2-text-muted)' }}>
        Showing <strong style={{ color: 'var(--pv2-text)' }}>{start.toLocaleString()}</strong> to <strong style={{ color: 'var(--pv2-text)' }}>{end.toLocaleString()}</strong> of <strong style={{ color: 'var(--pv2-text)' }}>{total.toLocaleString()}</strong> jobs
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button className="pv2-btn pv2-btn-ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: 8 }} data-testid={`${testIdPrefix}-prev`}>‹</button>
        <span style={{ fontSize: 12, color: 'var(--pv2-text-muted)' }}>Page</span>
        <select className="pv2-select" value={page} onChange={(e) => setPage(parseInt(e.target.value, 10))} style={{ padding: '6px 10px', width: 'auto' }} data-testid={`${testIdPrefix}-select`}>
          {Array.from({ length: pageCount }, (_, i) => i + 1).map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: 'var(--pv2-text-muted)' }}>of <strong style={{ color: 'var(--pv2-text)' }}>{pageCount}</strong></span>
        <button className="pv2-btn pv2-btn-ghost" disabled={page >= pageCount} onClick={() => setPage(p => p + 1)} style={{ padding: 8 }} data-testid={`${testIdPrefix}-next`}>›</button>
      </div>
    </div>
  );
};

export default AdminV2Jobs;
